import { useEffect, useState } from "react";
import { useAuthMe, useSignOut } from "@vorloop/api-client-react";
import { IconX, IconCog } from "../layout/Icons";

/**
 * Replit-style modal Settings dialog. Section tree on the left,
 * panel on the right. Sections grouped under "Workspace", "Account",
 * "User". Open via {@link useSettingsModalState}; close via × or Esc.
 *
 * The legacy /settings page still renders for direct-URL visits and
 * for smoke fixtures — the modal is the new canonical entrypoint
 * opened from the NavRail's Settings link + the command palette.
 */

export type SettingsSection =
  | "workspace-overview"
  | "workspace-collaborators"
  | "integrations"
  | "workspace-usage"
  | "security"
  | "billing"
  | "account-seats"
  | "account-usage"
  | "advanced"
  | "profile"
  | "promotions"
  | "personalization";

interface SectionMeta {
  id: SettingsSection;
  label: string;
  icon: string;
}

const GROUPS: ReadonlyArray<{ group: string; sections: ReadonlyArray<SectionMeta> }> = [
  {
    group: "Workspace",
    sections: [
      { id: "workspace-overview", label: "Workspace overview", icon: "▣" },
      { id: "workspace-collaborators", label: "Workspace collaborators", icon: "◉" },
      { id: "integrations", label: "Integrations", icon: "↔" },
      { id: "workspace-usage", label: "Workspace usage", icon: "▤" },
      { id: "security", label: "Security", icon: "◊" },
    ],
  },
  {
    group: "Account",
    sections: [
      { id: "billing", label: "Billing", icon: "▦" },
      { id: "account-seats", label: "Account seats", icon: "◫" },
      { id: "account-usage", label: "Account usage", icon: "◐" },
      { id: "advanced", label: "Advanced", icon: "◇" },
    ],
  },
  {
    group: "User",
    sections: [
      { id: "profile", label: "Profile", icon: "◯" },
      { id: "promotions", label: "Promotions & Referrals", icon: "★" },
      { id: "personalization", label: "Personalization", icon: "◑" },
    ],
  },
];

export function SettingsModal({
  open,
  onClose,
  initialSection = "workspace-overview",
}: {
  open: boolean;
  onClose: () => void;
  initialSection?: SettingsSection;
}) {
  const [section, setSection] = useState<SettingsSection>(initialSection);
  const me = useAuthMe();
  const handle = me.data?.user?.handle ?? "you";

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setSection(initialSection);
  }, [open, initialSection]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      data-testid="settings-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => {
        // Click outside the panel closes.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(960px, 92vw)",
          height: "min(640px, 88vh)",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 12,
          display: "flex",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
        }}
      >
        {/* Left nav */}
        <aside
          style={{
            width: 230,
            flexShrink: 0,
            borderRight: "1px solid var(--border-default)",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-panel-2)",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "14px 16px",
              fontSize: 15,
              fontWeight: 600,
              color: "var(--text-primary)",
              borderBottom: "1px solid var(--border-default)",
            }}
          >
            <IconCog />
            <span>Settings</span>
          </div>
          <div style={{ padding: "10px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                fontSize: 12,
                color: "var(--text-tertiary)",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: "var(--accent-orange, #f97316)",
                  color: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {handle.charAt(0)}
              </span>
              {handle}'s Workspace
            </div>
            {GROUPS.map((g) => (
              <section key={g.group} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <div
                  style={{
                    padding: "8px 8px 4px",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: "var(--text-tertiary)",
                  }}
                >
                  {g.group}
                </div>
                {g.sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSection(s.id)}
                    aria-current={section === s.id ? "page" : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      borderRadius: 6,
                      background: section === s.id ? "var(--bg-active)" : "transparent",
                      border: "none",
                      color: section === s.id ? "var(--text-primary)" : "var(--text-secondary)",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: 12.5,
                    }}
                  >
                    <span aria-hidden style={{ width: 14, textAlign: "center", color: "var(--text-tertiary)" }}>
                      {s.icon}
                    </span>
                    {s.label}
                  </button>
                ))}
              </section>
            ))}
          </div>
        </aside>

        {/* Right panel */}
        <section
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <header
            style={{
              display: "flex",
              alignItems: "center",
              padding: "12px 20px",
              borderBottom: "1px solid var(--border-default)",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 600 }}>{labelFor(section)}</span>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close settings"
              title="Close (Esc)"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "1px solid var(--border-default)",
                borderRadius: 8,
                color: "var(--text-secondary)",
                cursor: "pointer",
              }}
            >
              <IconX size={12} />
            </button>
          </header>
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
            <SectionPanel section={section} onSignOut={() => onClose()} />
          </div>
        </section>
      </div>
    </div>
  );
}

function labelFor(s: SettingsSection): string {
  for (const g of GROUPS) {
    const m = g.sections.find((x) => x.id === s);
    if (m) return m.label;
  }
  return s;
}

function SectionPanel({
  section,
  onSignOut,
}: {
  section: SettingsSection;
  onSignOut: () => void;
}) {
  const me = useAuthMe();
  const signOut = useSignOut({ onSuccess: onSignOut });
  const user = me.data?.user;

  switch (section) {
    case "workspace-overview":
      return <WorkspaceOverview handle={user?.handle ?? "you"} />;
    case "billing":
      return <Stub title="Plan & billing" body="Open the legacy /settings page to manage your plan while the modal flow ships. Stripe Checkout wiring is in apps/web/src/pages/Settings.tsx." />;
    case "profile":
      return <ProfilePanel handle={user?.handle ?? "—"} email={user?.email ?? "—"} tier={(user?.tier ?? "free").toUpperCase()} onSignOut={() => signOut.mutate()} signingOut={signOut.isPending} />;
    case "promotions":
      return <PromotionsPanel handle={user?.handle ?? "you"} />;
    case "personalization":
      return <Stub title="Personalization" body="Keyboard shortcuts editor lives on /settings — the modal port is coming." />;
    case "workspace-collaborators":
      return <Stub title="Workspace collaborators" body="Invite + role management lands when /api/workspaces/:id/members ships." />;
    case "integrations":
      return <Stub title="Integrations" body="GitHub, Stripe, OpenRouter, etc. — config UI lands with the live providers." />;
    case "workspace-usage":
      return <Stub title="Workspace usage" body="Token + minute meters land when the usage rollup ships." />;
    case "security":
      return <Stub title="Security" body="2FA + active sessions land in Wave 2c." />;
    case "account-seats":
      return <Stub title="Account seats" body="Multi-seat billing lands with Teams plan." />;
    case "account-usage":
      return <Stub title="Account usage" body="Per-account credit + minute breakdown." />;
    case "advanced":
      return <Stub title="Advanced" body="Delete account, export data, beta flags." />;
    default:
      return <Stub title="Coming soon" body="" />;
  }
}

function Stub({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ color: "var(--text-secondary)" }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: "var(--text-primary)" }}>
        {title}
      </h2>
      <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function WorkspaceOverview({ handle }: { handle: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>Workspace name</span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "10px 14px",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border-default)",
            borderRadius: 10,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: "var(--accent-orange, #f97316)",
              color: "#fff",
              fontSize: 24,
              fontWeight: 700,
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {handle.charAt(0)}
          </span>
          <input
            type="text"
            readOnly
            value={`${handle}'s workspace`}
            style={{
              flex: 1,
              padding: "8px 12px",
              background: "var(--bg-canvas)",
              border: "1px solid var(--border-default)",
              borderRadius: 8,
              color: "var(--text-primary)",
              fontSize: 13,
              outline: "none",
            }}
          />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>Export Apps</span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "12px 16px",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border-default)",
            borderRadius: 10,
          }}
        >
          <span style={{ flex: 1, fontSize: 12.5, color: "var(--text-secondary)" }}>
            Bulk export personal Apps. We'll send you a download link when ready.
          </span>
          <button
            type="button"
            disabled
            title="Coming soon — bulk project export"
            style={{
              padding: "7px 12px",
              background: "var(--bg-active)",
              border: "1px solid var(--border-default)",
              borderRadius: 8,
              color: "var(--text-tertiary)",
              fontSize: 12,
              cursor: "not-allowed",
            }}
          >
            ⤓ Start Export
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfilePanel({
  handle,
  email,
  tier,
  onSignOut,
  signingOut,
}: {
  handle: string;
  email: string;
  tier: string;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Handle" value={handle} />
      <Field label="Email" value={email} />
      <Field label="Tier" value={tier} pill />
      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        style={{
          alignSelf: "flex-start",
          padding: "8px 14px",
          background: signingOut ? "var(--bg-active)" : "var(--accent-red)",
          color: signingOut ? "var(--text-tertiary)" : "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          cursor: signingOut ? "not-allowed" : "pointer",
          marginTop: 12,
        }}
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}

function Field({ label, value, pill }: { label: string; value: string; pill?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
      <span style={{ fontSize: 12.5, color: "var(--text-tertiary)" }}>{label}</span>
      <span
        style={{
          fontSize: 12.5,
          color: "var(--text-primary)",
          ...(pill && {
            padding: "2px 10px",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border-default)",
            borderRadius: 999,
            fontWeight: 600,
            letterSpacing: 0.4,
          }),
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PromotionsPanel({ handle }: { handle: string }) {
  const url = `${window.location.origin}/refer/${encodeURIComponent(handle)}?trackingContext=universal-settings-modal`;
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {
        // ignore
      },
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Referrals</h2>
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-secondary)" }}>
          Bring your friends to Vor. They get $20 when they upgrade, and so do
          you once you're on a paid plan.
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
          }}
        >
          <input
            type="text"
            readOnly
            value={url}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--text-primary)",
              fontSize: 12,
            }}
          />
          <button
            type="button"
            onClick={copy}
            data-testid="copy-referral-link"
            style={{
              padding: "5px 12px",
              background: "var(--accent-blue)",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 32, paddingTop: 4 }}>
          <Stat value="$0" label="Credits earned" />
          <Stat value="0" label="Completed referrals" />
        </div>
      </section>
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Perks</h2>
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-secondary)" }}>
          Claim discounts and benefits from our partners.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          {PERKS.map((p) => (
            <article
              key={p.title}
              style={{
                padding: 12,
                background: "var(--bg-panel-2)",
                border: "1px solid var(--border-default)",
                borderRadius: 10,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: p.swatch,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {p.title.charAt(0)}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{p.title}</span>
              <span style={{ fontSize: 11.5, color: "var(--text-tertiary)", lineHeight: 1.45 }}>
                {p.blurb}
              </span>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>{value}</span>
      <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{label}</span>
    </div>
  );
}

const PERKS: ReadonlyArray<{ title: string; blurb: string; swatch: string }> = [
  {
    title: "Stripe Atlas",
    blurb:
      "Incorporate your Delaware C-corp and get 20% off the $500 incorporation fee — EIN, 83(b), equity issuance, and $2,500 in credits.",
    swatch: "linear-gradient(135deg, #635bff, #4a3eff)",
  },
  {
    title: "Mercury",
    blurb:
      "Open a Mercury business bank account and earn $250 cash when you deposit $5K within the first 90 days.",
    swatch: "linear-gradient(135deg, #5b6bff, #1f2bff)",
  },
  {
    title: "QuickBooks",
    blurb: "Manage your business finances with QuickBooks and get 50% off for 3 months.",
    swatch: "linear-gradient(135deg, #2ca01c, #1e6e10)",
  },
];
